import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

const clampWidth = (value, minWidth, maxWidth) =>
  Math.max(minWidth, Math.min(maxWidth, value));

const readStoredWidth = (storageKey, fallback) => {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(storageKey);
  const parsed = stored ? Number(stored) : fallback;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const ResizableSidebar = ({
  storageKey,
  defaultWidth = 440,
  minWidth = 420,
  maxWidth = 480,
  className,
  handleClassName,
  handlePosition = "right",
  handleTitle = "Drag to resize",
  children,
}) => {
  const [width, setWidth] = useState(() =>
    clampWidth(readStoredWidth(storageKey, defaultWidth), minWidth, maxWidth),
  );

  const styleVars = useMemo(
    () => ({
      "--sidebar-width": `${width}px`,
      "--sidebar-min": `${minWidth}px`,
      "--sidebar-max": `${maxWidth}px`,
    }),
    [maxWidth, minWidth, width],
  );

  const onMouseDown = useCallback(
    (event) => {
      const startX = event.clientX;
      const startWidth = width;
      const direction = handlePosition === "left" ? -1 : 1;

      const handleMove = (moveEvent) => {
        const delta = (moveEvent.clientX - startX) * direction;
        const nextWidth = clampWidth(startWidth + delta, minWidth, maxWidth);
        setWidth(nextWidth);
        if (storageKey) {
          window.localStorage.setItem(storageKey, String(nextWidth));
        }
      };

      const handleUp = () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
    },
    [handlePosition, maxWidth, minWidth, storageKey, width],
  );

  return (
    <div
      className={cn(
        "relative w-full min-w-0 lg:flex-shrink-0 lg:w-[var(--sidebar-width)] lg:min-w-[var(--sidebar-min)] lg:max-w-[var(--sidebar-max)]",
        className,
      )}
      style={styleVars}
    >
      {children}
      <div
        role="separator"
        aria-label={handleTitle}
        className={cn(
          "absolute top-0 z-10 hidden h-full w-3 cursor-col-resize lg:block",
          handlePosition === "left" ? "left-[-6px]" : "right-[-6px]",
          handleClassName,
        )}
        onMouseDown={onMouseDown}
        title={handleTitle}
      />
    </div>
  );
};

export default ResizableSidebar;
